import React, { useEffect, useState } from 'react';
import { Image } from 'antd';
import classNames from 'classnames';
import { MessageQuestionIcon } from 'icons';

import { useAppDispatch } from 'store';

import { setQuestionId, setTestFeedbackModal } from 'modules/global';

import { MathJaxContext } from 'components';

import { useCheckTest } from '../hooks';
import { IDTMQuestion } from '../models';

interface IProps {
  test?: IDTMQuestion;
  testName?: string;
  currentQuestion?: number;
  questionsLength?: number;
  onAnswer?: (questionId: number) => void;
}

export const QuizCard: React.FC<IProps> = ({ test, testName, currentQuestion, questionsLength, onAnswer }) => {
  const dispatch = useAppDispatch();
  const [selectedOption, setSelectedOption] = useState<number | null | undefined>(null);

  const { mutate } = useCheckTest();

  const handleOptionSelect = (optionId?: number | null) => () => {
    if (optionId) {
      setSelectedOption(optionId);
    }

    if (optionId && test?.id && !selectedOption) {
      onAnswer?.(test.id);
    }

    mutate({
      testId: test?.id,
      optionId
    });
  };

  const onOpenFeedbackModal = () => {
    dispatch(setQuestionId(test?.question_id as number));
    dispatch(setTestFeedbackModal(true));
  };

  useEffect(() => {
    const preselected = test?.options?.find(opt => opt.is_selected);

    if (preselected) setSelectedOption(preselected.id);
  }, [test]);

  return (
    <>
      <div className="shadow-xs mb-3 rounded-xl bg-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {currentQuestion}/{questionsLength} • {testName}
          </div>
          <div onClick={onOpenFeedbackModal} className="flex cursor-pointer items-center gap-2 text-orange-400">
            <MessageQuestionIcon />
          </div>
        </div>
        <div className="mt-3 min-w-0 overflow-x-auto overflow-y-hidden">
          <div className="inline-block whitespace-pre-line text-sm font-medium">
            <MathJaxContext expression={test?.question_name} />
          </div>
        </div>
        {test?.image && (
          <div className="flex justify-center">
            <Image src={test.image} alt={test?.name} loading="lazy" title="Test image" />
          </div>
        )}
      </div>

      <div className="shadow-xs mb-4 rounded-xl bg-white px-4 py-3">
        <div className="space-y-3">
          {test?.options?.map((option, index) => {
            const isSelected = selectedOption === option.id;

            return (
              <div
                key={option.id}
                className={classNames('rounded-xl border-2', {
                  'border-transparent': !isSelected,
                  'border-green-500 bg-green-50': isSelected
                })}
              >
                <div
                  className={classNames('flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-all', {
                    'border-gray-200': !isSelected,
                    'border-transparent': isSelected
                  })}
                  onClick={handleOptionSelect(option?.id)}
                >
                  <span className="font-semibold text-gray-600">{String.fromCharCode(97 + index)})</span>
                  <div className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
                    <p className="inline-block whitespace-pre-line text-xs text-gray-800">
                      <MathJaxContext expression={option?.name} />
                    </p>
                    {option?.image && (
                      <div className="mt-2 flex justify-center">
                        <Image
                          src={option?.image}
                          alt={option?.name || 'Test option'}
                          loading="lazy"
                          title="Test image"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};
